/**
 * AgentsPanel.tsx — the bundle-level global-agents manager (Phase D).
 *
 * Lists `bundle.agents[]` — agents attached to the whole bundle rather than to
 * a single node/edge. Each global agent sees the full tick + params + bundle
 * when the runner invokes it. The panel reuses `AgentModal` for editing a
 * single agent; the host is built from the bundle's full param space (every
 * node + edge param) so the capability fence pills cover every writable knob.
 *
 * Opened from the MenuBar's File → "Global agents…" entry. Modal-shaped so it
 * layers above the canvas the same way LibraryModal does.
 */
import { useMemo, useState } from 'react';
import { Plus, Plugs, Trash } from '@phosphor-icons/react';

import type { AgentConfig, ScenarioBundle } from '../engine/types';
import { Modal } from './Modal';
import { AgentModal, type AgentParamOption } from './AgentModal';
import type { LibraryAdapter } from '../library/LibraryAdapter';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';

interface AgentsPanelProps {
  bundle: ScenarioBundle;
  editable: boolean;
  libraryAdapter?: LibraryAdapter;
  /** Project backend for the Code-tab source-file picker. Optional — when
   *  absent, the Code tab falls back to the library picker / free text. */
  projectAdapter?: RemoteProjectAdapter;
  /** The project id whose `src/` to list in the Code-tab picker. */
  projectId?: string;
  onClose: () => void;
  /** Replace the bundle's whole global-agents list. The App threads this to
   *  the adapter's editor surface (LocalAdapter fires onBundleChange). */
  onChange: (agents: AgentConfig[] | undefined) => void;
}

/** Build the full param-path space a global agent may write — every node +
 *  edge param flattened to its setParam path. Used for the capability-fence
 *  pills in the AgentModal. */
export function globalParamOptions(bundle: ScenarioBundle): AgentParamOption[] {
  const opts: AgentParamOption[] = [];
  for (const n of bundle.graph.nodes) {
    const all = [...n.params, ...(n.gate?.params ?? [])];
    for (const p of all) {
      opts.push({ key: p.key, label: `${n.label || n.id}.${p.label || p.key}`, path: `${n.id}.${p.key}` });
    }
  }
  for (const e of bundle.graph.edges) {
    for (const p of e.params) {
      opts.push({ key: p.key, label: `e:${e.id}.${p.label || p.key}`, path: `e:${e.id}.${p.key}` });
    }
  }
  return opts;
}

export function AgentsPanel({
  bundle,
  editable,
  libraryAdapter,
  projectAdapter,
  projectId,
  onClose,
  onChange,
}: AgentsPanelProps): JSX.Element {
  const agents = bundle.agents ?? [];
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // In-progress config staged while the modal is open. The modal's `onChange`
  // fires on every keystroke/toggle; staging here (instead of committing +
  // closing on each one) keeps the modal open through edits. The bundle updates
  // once, on close — matches NodeModal's draft-then-save pattern.
  const [draft, setDraft] = useState<AgentConfig | undefined>(undefined);
  // "create" is signalled by editingIndex === -1; null = list view.
  const paramOptions = useMemo(() => globalParamOptions(bundle), [bundle]);

  const commitList = (next: AgentConfig[]) => {
    // Drop an empty trailing entry; undefined when the list empties out so we
    // don't persist `agents: []` on the bundle (mirrors other optional fields).
    const filtered = next.filter((a) => a && a.target);
    onChange(filtered.length > 0 ? filtered : undefined);
  };

  const addAgent = () => {
    setDraft(undefined);
    setEditingIndex(-1);
  };
  const editAgent = (i: number) => {
    setDraft(undefined);
    setEditingIndex(i);
  };
  const removeAgent = (i: number) => {
    const next = agents.slice();
    next.splice(i, 1);
    commitList(next);
  };

  // ─── Editor view (single AgentModal) ────────────────────────────────────
  if (editingIndex !== null) {
    const creating = editingIndex < 0;
    const config = creating ? draft : (draft ?? agents[editingIndex]);
    // Commit the staged draft to the bundle and close. Apply-on-close, not
    // apply-on-every-keystroke — the modal's `onChange` only stages into
    // `draft` so it stays open through every field interaction.
    const closeEditor = () => {
      if (draft && draft.target) {
        const scoped: AgentConfig = { ...draft, scope: 'global' };
        if (creating) {
          commitList([...agents, scoped]);
        } else {
          const next = agents.slice();
          next[editingIndex] = scoped;
          commitList(next);
        }
      }
      setDraft(undefined);
      setEditingIndex(null);
    };
    return (
      <AgentModal
        host={{
          title: creating ? 'New global agent' : `Global agent ${editingIndex + 1}`,
          paramOptions,
          scope: 'global',
          inputDescription:
            'A global agent receives the full tick (every node + edge value), the complete resolved params map, and the bundle itself, every tick. It runs after all node/edge-local agents.',
        }}
        config={config}
        editable={editable}
        libraryAdapter={libraryAdapter}
        projectAdapter={projectAdapter}
        projectId={projectId}
        onClose={closeEditor}
        onChange={(cfg) => {
          if (cfg === undefined) {
            // AgentModal's "Delete agent" — only valid when editing an existing entry.
            if (!creating) removeAgent(editingIndex);
            setDraft(undefined);
            setEditingIndex(null);
            return;
          }
          // Stage only — scope forced to global on commit (closeEditor).
          setDraft(cfg);
        }}
      />
    );
  }

  // ─── List view ──────────────────────────────────────────────────────────
  return (
    <Modal title="Global agents" onClose={onClose}>
      <div className="agents-panel">
        <p className="agents-panel-hint">
          Global agents run after every node/edge-local agent on each tick. Each sees the full tick
          + params + bundle. Configure which knobs it may write via its capability fence.
        </p>
        <ul className="agents-list">
          {agents.length === 0 && (
            <li className="agents-empty">No global agents configured.</li>
          )}
          {agents.map((a, i) => {
            const writable = Object.keys(a.outputMapping ?? {});
            const readOnly = writable.length === 0;
            const mode = a.mode ?? 'api';
            return (
              <li key={`${a.target}-${i}`} className="agents-row">
                <button
                  type="button"
                  className="agents-row-main"
                  onClick={() => editAgent(i)}
                  title={editable ? 'Edit' : 'View'}
                >
                  <Plugs size={16} />
                  <div className="agents-row-text">
                    <div className="agents-row-target">
                      {a.name ? `${a.name} — ` : ''}{a.target || '(unset)'}
                    </div>
                    <div className="agents-row-meta">
                      <span className="agents-mode">{mode}</span>
                      {readOnly
                        ? <span className="agents-fence read-only">read-only</span>
                        : <span className="agents-fence">{writable.length} knob{writable.length === 1 ? '' : 's'}</span>}
                    </div>
                  </div>
                </button>
                {editable && (
                  <button
                    type="button"
                    className="agents-row-del"
                    title="Remove"
                    onClick={() => removeAgent(i)}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {editable && (
          <button type="button" className="btn agents-add" onClick={addAgent}>
            <Plus size={14} /> Add global agent
          </button>
        )}
      </div>
    </Modal>
  );
}
